import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { ICON_COLOR, Tooltip } from '@zep-us/zus';
import { XIcon } from '@zep-us/zus-icons';
import { getCookie, setCookie } from 'cookies-next';
const DEFAULT_SNOOZE_DAYS = 7;
interface DismissibleTooltipProps {
  /** Whether the tooltip is eligible to open at all (auth/flag gates). */
  eligible: boolean;
  /** Unique cookie key used to persist the dismissed state. */
  cookieKey: string;
  /** Days to keep the tooltip hidden after the user dismisses it. Default 7. */
  snoozeDays?: number;
  /** Tooltip body content (plain string or ReactNode). */
  content: ReactNode;
  /** Trigger element (button/link etc.). */
  children: ReactNode;
}

/**
 * 다음 자정 기준 D+N일 Date를 계산 (스누즈 만료)
 * 로컬(브라우저) 시간대 기준 자정으로 정규화.
 */
function getSnoozeExpiry(days: number): Date {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + days);
  expiry.setHours(0, 0, 0, 0);
  return expiry;
}

/**
 * 하단 인버스 툴팁 + "X" 닫기 + N일 쿠키 스누즈 기본 로직.
 *
 * eligible=false이면 트리거만 렌더링하고 툴팁은 숨김.
 * X 클릭 시 해당 cookieKey에 스누즈 플래그를 저장하여 snoozeDays 동안 재노출 차단.
 *
 * z-modal로 올려 iframe 등 동일 stacking 레벨 위로 그려진다. (Portal 없이 CoachMarkTooltip과 동일 패턴)
 */
export function DismissibleTooltip({
  eligible,
  cookieKey,
  snoozeDays = DEFAULT_SNOOZE_DAYS,
  content,
  children
}: DismissibleTooltipProps) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const isSnoozed = !!getCookie(cookieKey);
    setOpen(eligible && !isSnoozed);
  }, [eligible, cookieKey]);
  const handleDismiss = useCallback(() => {
    setCookie(cookieKey, 'true', {
      expires: getSnoozeExpiry(snoozeDays),
      path: '/'
    });
    setOpen(false);
  }, [cookieKey, snoozeDays]);
  if (!eligible) return <>{children}</>;
  return <Tooltip.Provider delayDuration={0} data-sentry-element="unknown" data-sentry-component="DismissibleTooltip" data-sentry-source-file="DismissibleTooltip.tsx">
      <Tooltip.Root open={open} data-sentry-element="unknown" data-sentry-source-file="DismissibleTooltip.tsx">
        <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="DismissibleTooltip.tsx">{children}</Tooltip.Trigger>
        <Tooltip.Content side="bottom" sideOffset={4} className="z-modal flex max-w-[300px] items-center gap-3xs rounded-2xs bg-[rgba(28,27,31,0.8)] px-2xs py-2xs shadow-xl" onPointerDownOutside={e => e.preventDefault()} data-sentry-element="unknown" data-sentry-source-file="DismissibleTooltip.tsx">
          <span className="text-body-sm-semibold text-white">{content}</span>
          <button type="button" onClick={handleDismiss} aria-label="close" className="flex items-center justify-center">
            <XIcon size="xs" fill={ICON_COLOR.inverse} data-sentry-element="XIcon" data-sentry-source-file="DismissibleTooltip.tsx" />
          </button>
          <Tooltip.Arrow className="fill-[rgba(28,27,31,0.8)]" data-sentry-element="unknown" data-sentry-source-file="DismissibleTooltip.tsx" />
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>;
}